import {Component, inject, input, OnChanges, OnInit} from '@angular/core'
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms'
import {MatButton} from '@angular/material/button'
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field'
import {MatInput} from '@angular/material/input'
import {Property} from '@sparbanken-syd/kalpylator'
import {FormatNumberDirective, getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {merge, startWith} from 'rxjs'
import {BorgoService} from '../../../services/borgo.service'
import {KalpService} from '../../../services/kalp.service'
import {getProperty} from '../../../services/types'
import {StepComponent} from '../90-step/step.component'
import {PropertyComponent} from '../98-property/property.component'
import {PropertyTypeName, propertyTypeNames} from '../application-types'

@Component({
  selector: 'spb-new-home',
  templateUrl: './new-home.component.html',
  styleUrls: ['./new-home.component.scss'],
  imports: [
    ReactiveFormsModule,
    PropertyComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatButton,
    FormatNumberDirective
  ]
})
export class NewHomeComponent extends StepComponent implements OnInit, OnChanges {
  readonly property = input<Property>(getProperty())

  /**
   * This is the complete list including (possibly) the main building that we hide for listing purposes.
   */
  readonly properties = input<Property[]>([])

  public filteredPropertyTypes: PropertyTypeName[] = propertyTypeNames.filter(p => p.type !== 'HYRES')

  public allPropertyTypes: PropertyTypeName[] = propertyTypeNames

  public form = new FormGroup({
    loanAmount: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.min(1)]
    }),
    mortgageRequirementValue: new FormControl<number>(0, {nonNullable: true}),
    mortgageRequirementDebt: new FormControl<number>(0, {nonNullable: true}),
    marketValue: new FormControl<number>(0, {nonNullable: true})
  })

  public propertyValidCtrl = new FormControl<boolean>(false, {
    nonNullable: true,
    validators: [Validators.requiredTrue]
  })

  public otherPropertiesValidCtrl: FormArray = new FormArray<any>([])

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()
  private readonly borgoService = inject(BorgoService)
  private readonly kalpService = inject(KalpService)

  constructor() {
    super()
  }

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(startWith({}))
      .subscribe((val: any) => {
        const property = this.property()
        if (property) {
          Object.assign(property, val)
        }
      })

    this.form.controls.mortgageRequirementValue.valueChanges.subscribe(val => {
      if (this.form.controls.loanAmount.pristine) {
        this.form.controls.loanAmount.setValue(val * 0.9)
      }

      // We asume that the market value is the same as mortgage requirement,
      // needed for calculations in kalpylator
      this.form.controls.marketValue.setValue(val)
    })

    this.form.controls.loanAmount.valueChanges.subscribe(val => {
      if (this.form.controls.mortgageRequirementDebt.pristine) {
        this.form.controls.mortgageRequirementDebt.setValue(+val)
      }
    })

    merge(this.form.statusChanges, this.propertyValidCtrl.statusChanges,
      this.otherPropertiesValidCtrl.statusChanges)
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.validControl()
          .setValue(this.form.valid && this.propertyValidCtrl.valid)
      })
  }

  public ngOnChanges(): void {
    const property = this.property()
    if (property?.id) {
      this.form.controls.loanAmount.addValidators(Validators.min(1))
      // @Erik - Sonar plugin ? {...property}
      const propToForm = {...property}
      this.form.reset()
      this.form.patchValue(propToForm)
    } else {
      this.form.controls.loanAmount.setValidators([])
      this.form.reset()
    }
    this.form.updateValueAndValidity()
    // Clear the controls and add validators for each
    this.otherPropertiesValidCtrl.clear({emitEvent: false})
    this.properties()
      .forEach((p: Property) => {
        const ctrl: AbstractControl = this.getValidControl()
        // Since the primary is hidden, we for this purpose assume it is valid.
        ctrl.setValue(p.primary)
        this.otherPropertiesValidCtrl.push(ctrl)
      })
  }

  public addProperty(): void {
    const borgoOrSparbanken = this.borgoService.domain()
    const addedProperty = getProperty()
    this.kalpService.propertyDefaults(addedProperty, borgoOrSparbanken)
    this.properties().push(addedProperty)
    this.otherPropertiesValidCtrl.push(this.getValidControl())
  }

  public removeProperty = (index: number): void => {
    this.properties().splice(index, 1)
    this.otherPropertiesValidCtrl.removeAt(index)
  }

  private getValidControl(): FormControl {
    return new FormControl<boolean>(false,
      {nonNullable: true, validators: [Validators.requiredTrue]})
  }
}
